import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  LinearProgress, 
  Paper, 
  Grid, 
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ImagePreview from './ImagePreview';
import FolderView from './FolderView';
import apiService from '../services/api';
import {
  buildUploadImagesFromResult,
  buildLocalPreviewFromFiles,
  createUploadSession,
  deleteUploadSession,
  findLatestPendingUploadSession,
  isUploadNetworkError,
  loadStoredUploadFile,
  loadUploadSession,
  sortUploadFilesByName,
  summarizeUploadSession,
  updateUploadFile,
  updateUploadSession,
} from '../utils/persistentUploads';

const UploadBox = styled(Box)(({ theme }) => ({
  border: '2px dashed #ccc',
  borderRadius: '8px',
  padding: '40px',
  textAlign: 'center',
  backgroundColor: '#fafafa',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: '#f0f7ff',
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const sortElevationKeys = (keys = []) => [...keys].sort((a, b) => {
  const aMatch = String(a).match(/^(\d+)/);
  const bMatch = String(b).match(/^(\d+)/);
  if (aMatch && bMatch) {
    const numA = parseInt(aMatch[1], 10);
    const numB = parseInt(bMatch[1], 10);
    if (numA !== numB) return numA - numB;
    return String(a).localeCompare(String(b));
  }
  if (aMatch && !bMatch) return -1;
  if (!aMatch && bMatch) return 1;
  const aMatchE = String(a).match(/E(\d+)/i);
  const bMatchE = String(b).match(/E(\d+)/i);
  if (aMatchE && bMatchE) {
    return parseInt(aMatchE[1], 10) - parseInt(bMatchE[1], 10);
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
});

const ImageUpload = ({ onFolderStructureChange, onNavigate, elevationName = null, projectId = null, projectName = null, buildingId = null, initialImageType = null }) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [folderStructure, setFolderStructure] = useState({});
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSession, setUploadSession] = useState(null);
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuildingId, setSelectedBuildingId] = useState('');
  const [buildingsLoading, setBuildingsLoading] = useState(false);
  const [projectType, setProjectType] = useState(null);
  const [activeImageType, setActiveImageType] = useState(initialImageType || 'drone');
  const [showUploadBox, setShowUploadBox] = useState(true);
  const [keyFindingsMap, setKeyFindingsMap] = useState(() => {
    try {
      return [];
    } catch {
      return [];
    }
  });
  const activeUploadRef = useRef(null);
  const isMountedRef = useRef(true);
  const folderStructureRef = useRef(folderStructure);
  const previewObjectUrlsRef = useRef([]);
  
  // API service is imported as singleton

  // Fetch project details to determine type
  useEffect(() => {
    if (projectId) {
      const fetchProjectDetails = async () => {
        try {
          const project = await apiService.getProject(projectId);
          if (project) {
            setProjectType(project.project_type);
            // Set default image type based on project type
            if (initialImageType) {
              setActiveImageType(initialImageType);
            } else if (project.project_type === 'Tapping Only') {
              setActiveImageType('tapping');
            } else {
              setActiveImageType('drone');
            }
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
        }
      };
      fetchProjectDetails();
    }
  }, [projectId, initialImageType]);

  // Fetch buildings when projectId changes
  useEffect(() => {
    if (projectId) {
      fetchBuildings();
    }
  }, [projectId]);

  // Preselect building if provided via props
  useEffect(() => {
    if (buildingId) {
      setSelectedBuildingId(buildingId);
    }
  }, [buildingId]);
  
  // Load key findings from API when building or image type changes
  useEffect(() => {
    const bid = selectedBuildingId || buildingId;
    if (!bid) return;
    (async () => {
      try {
        const resp = await apiService.listKeyFindings(bid, activeImageType);
        const ids = (resp.items || []).map((r) => String(r.image_id));
        setKeyFindingsMap(ids);
      } catch (e) {
        setKeyFindingsMap([]);
      }
    })();
  }, [selectedBuildingId, buildingId, activeImageType]);

  // Load existing images/folder structure on mount or when project/building changes
  useEffect(() => {
    const loadExisting = async () => {
      try {
        // Avoid interfering while an active upload is in progress
        if (isUploading) return;
        const previewContext = {
          projectId,
          buildingId: selectedBuildingId || buildingId || null,
          imageType: activeImageType,
        };
        const pendingSession = await findLatestPendingUploadSession(previewContext);
        if (pendingSession) {
          await hydratePreviewSession(pendingSession);
          const summary = summarizeUploadSession(pendingSession);
          setUploadSession(summary);
          return;
        }

        let response;
        const effectiveBuildingId = selectedBuildingId || buildingId;
        if (effectiveBuildingId) {
          // Pass activeImageType to filter images
          response = await apiService.getBuildingImages(effectiveBuildingId, activeImageType);
        } else if (projectId) {
          response = await apiService.getProjectImages(projectId);
          // If we got project images (which might be mixed), we might want to filter them client-side
          // if the backend doesn't support filtering for getProjectImages yet.
          // However, for now, we'll assume the user focuses on building-level uploads.
        } else {
          response = await apiService.getFolderStructure();
        }

        const backendFolder = response?.folder_structure || {};
        if (Object.keys(backendFolder).length === 0) {
          setFolderStructure({});
          setUploadedImages([]);
          setSelectedFolder(null);
          setShowUploadBox(true);
          return;
        }

        const newImages = [];
        let newFolderStructure = {};
        Object.keys(backendFolder).forEach((elevationKey) => {
          const images = backendFolder[elevationKey] || [];
          images.forEach((backendImage, index) => {
            const imageObject = {
              id: backendImage.id,
              src: apiService.getImageUrl(backendImage.id),
              elevation: elevationKey || elevationName || 'Unsorted',
              sourceElevationName: elevationKey || elevationName || 'Unsorted',
              originalElevationName: elevationKey || elevationName || 'Unsorted',
              floor: backendImage.floor_name || `Floor ${index % 5 + 1}`,
              filename: backendImage.filename,
              url: backendImage.url,
              folderPath: elevationKey || elevationName || 'Unsorted',
              folderName: elevationKey || elevationName || 'Unsorted',
              projectName: projectName || (response?.project_name || 'Project'),
              uploadStatus: 'server',
              uploadSource: 'server',
            };
            newImages.push(imageObject);
            if (!newFolderStructure[elevationKey]) {
              newFolderStructure[elevationKey] = [];
            }
            newFolderStructure[elevationKey].push(imageObject);
          });
        });

        // Apply any saved folder rename mappings
        try {
          const renameKey = selectedBuildingId
            ? `folder_renames_building_${selectedBuildingId}`
            : (projectId ? `folder_renames_project_${projectId}` : 'folder_renames');
          const raw = localStorage.getItem(renameKey);
          if (raw) {
            const map = JSON.parse(raw);
            const remapped = {};
            Object.entries(newFolderStructure).forEach(([key, arr]) => {
              const target = map[key] || key;
              const updatedArr = (arr || []).map(img => ({
                ...img,
                elevation: target,
                folderName: target,
                folderPath: target,
                sourceElevationName: img.sourceElevationName || key,
                originalElevationName: img.originalElevationName || img.sourceElevationName || key,
              }));
              if (!remapped[target]) remapped[target] = [];
              remapped[target] = remapped[target].concat(updatedArr);
            });
            newFolderStructure = remapped;
          }
        } catch {}

        setFolderStructure(newFolderStructure);
        setUploadedImages(newImages);
        releasePreviewObjectUrls();
        setUploadSession(null);
        // Always select the first tab (sorted consistently with FolderView) on first load
        const sortedKeys = sortElevationKeys(Object.keys(newFolderStructure));
        setSelectedFolder(sortedKeys.length > 0 ? sortedKeys[0] : null);
      } catch (err) {
        console.error('Error loading existing images:', err);
      }
    };

    loadExisting();
  }, [projectId, selectedBuildingId, isUploading, elevationName, projectName, activeImageType]);

  const fetchBuildings = async () => {
    try {
      setBuildingsLoading(true);
      const response = await apiService.getProjectBuildings(projectId);
      setBuildings(response || []);
    } catch (error) {
      console.error('Error fetching buildings:', error);
      setBuildings([]);
    } finally {
      setBuildingsLoading(false);
    }
  };

  const releasePreviewObjectUrls = () => {
    previewObjectUrlsRef.current.forEach((url) => {
      try {
        if (url && typeof URL !== 'undefined' && URL.revokeObjectURL) {
          URL.revokeObjectURL(url);
        }
      } catch {}
    });
    previewObjectUrlsRef.current = [];
  };

  const setPreviewFromFiles = async (sessionId, files, sessionFileIds = []) => {
    releasePreviewObjectUrls();
    const preview = buildLocalPreviewFromFiles(files, {
      projectName: projectName || 'Uploaded Project',
      fallbackElevation: elevationName || 'Unsorted',
      fileMeta: sessionFileIds.map((id) => ({ id, status: 'pending' })),
      getObjectUrl: (file) => {
        const url = URL.createObjectURL(file);
        previewObjectUrlsRef.current.push(url);
        return url;
      },
    });

    if (!isMountedRef.current) return preview;

    setFolderStructure(preview.folderStructure);
    setUploadedImages(preview.images);
    setSelectedFolder(sortElevationKeys(Object.keys(preview.folderStructure || {}))[0] || null);
    setShowUploadBox(false);
    setUploadProgress(0);
    return preview;
  };

  const hydratePreviewSession = async (session) => {
    if (!session?.id) return null;
    const fileIds = Array.isArray(session.files) ? session.files.map((item) => item.id) : [];
    const storedFiles = await Promise.all(fileIds.map((fileId) => loadStoredUploadFile(session.id, fileId)));
    const previewFiles = storedFiles.filter(Boolean);
    releasePreviewObjectUrls();
    const preview = buildLocalPreviewFromFiles(previewFiles, {
      projectName: projectName || 'Uploaded Project',
      fallbackElevation: elevationName || 'Unsorted',
      fileMeta: session.files || [],
      getObjectUrl: (file) => {
        const url = URL.createObjectURL(file);
        previewObjectUrlsRef.current.push(url);
        return url;
      },
      getServerImageUrl: (imageId) => apiService.getImageUrl(imageId),
    });
    if (!isMountedRef.current) return preview;
    setFolderStructure(preview.folderStructure);
    setUploadedImages(preview.images);
    setSelectedFolder(sortElevationKeys(Object.keys(preview.folderStructure || {}))[0] || null);
    setShowUploadBox(false);
    setUploadProgress(summarizeUploadSession(session).percent);
    return preview;
  };

  const getEffectiveBuildingId = () => selectedBuildingId || buildingId || null;

  const refreshUploadSessionState = async (sessionId) => {
    if (!sessionId) return null;
    const freshSession = await loadUploadSession(sessionId);
    if (!freshSession) {
      if (isMountedRef.current) {
        setUploadSession(null);
        setUploadProgress(0);
      }
      return null;
    }
    const summary = summarizeUploadSession(freshSession);
    if (isMountedRef.current) {
      setUploadSession(summary);
      setUploadProgress(summary.percent);
    }
    return summary;
  };

  const processUploadSession = async (sessionId) => {
    if (!sessionId) return { completed: false, sessionId };
    if (activeUploadRef.current && activeUploadRef.current !== sessionId) {
      return { completed: false, sessionId };
    }

    activeUploadRef.current = sessionId;
    setIsUploading(true);

    try {
      await updateUploadSession(sessionId, { status: 'uploading' }).catch(() => {});
      let session = await loadUploadSession(sessionId);
      if (!session) return { completed: false, sessionId };

      for (const fileRecord of session.files || []) {
        session = await loadUploadSession(sessionId);
        if (!session) break;
        const latestFile = (session.files || []).find((file) => String(file.id) === String(fileRecord.id));
        if (!latestFile || latestFile.status === 'completed') {
          continue;
        }

        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          await updateUploadFile(sessionId, latestFile.id, { status: 'failed', error: 'Connection lost' }).catch(() => {});
          await updateUploadSession(sessionId, { status: 'paused', error: 'Connection lost' }).catch(() => {});
          await refreshUploadSessionState(sessionId);
          break;
        }

        await updateUploadFile(sessionId, latestFile.id, { status: 'uploading', error: '' }).catch(() => {});
        await refreshUploadSessionState(sessionId);

        try {
          const storedFile = await loadStoredUploadFile(sessionId, latestFile.id);
          if (!storedFile) {
            throw new Error('Could not load cached file from browser storage');
          }

          const uploadResult = await apiService.uploadFolderFile(
            storedFile,
            elevationName || null,
            projectId,
            getEffectiveBuildingId(),
            activeImageType
          );

          const normalized = buildUploadImagesFromResult(uploadResult, {
            fallbackElevation: elevationName || latestFile.estimatedElevation || 'Unsorted',
            projectName: projectName || 'Uploaded Project',
            getImageUrl: (imageId) => apiService.getImageUrl(imageId),
          });
          const normalizedImages = normalized.images || [];

          if (normalizedImages.length > 0) {
            setFolderStructure((prev) => {
              const next = { ...prev };
              const removeLocalPreview = (items = []) => items.filter((img) => {
                if (!img) return false;
                if (String(img.id) === String(latestFile.id)) return false;
                if (img.localPreview && String(img.filename || '') === String(latestFile.name || '')) return false;
                return true;
              });
              Object.keys(next).forEach((folderKey) => {
                next[folderKey] = removeLocalPreview(next[folderKey]);
              });
              Object.entries(normalized.folderStructure || {}).forEach(([folderKey, images]) => {
                const existing = next[folderKey] || [];
                next[folderKey] = [...existing, ...images];
              });
              return next;
            });

            setUploadedImages((prev) => {
              const next = (prev || []).filter((img) => {
                if (!img) return true;
                if (String(img.id) === String(latestFile.id)) return false;
                if (img.localPreview && String(img.filename || '') === String(latestFile.name || '')) return false;
                return true;
              });
              normalizedImages.forEach((img) => next.push(img));
              return next;
            });

            const folderKeys = Object.keys(normalized.folderStructure || {});
            if (folderKeys.length > 0) {
              setSelectedFolder((current) => current || folderKeys[0]);
            }
            setShowUploadBox(false);
          }

          const uploadedElevation = normalizedImages[0]?.elevation || latestFile.elevationName || latestFile.estimatedElevation || 'Unsorted';
          await updateUploadFile(sessionId, latestFile.id, {
            status: 'completed',
            error: '',
            elevationName: uploadedElevation,
            progress: 100,
            serverImageId: normalizedImages[0]?.id || null,
            serverUrl: normalizedImages[0]?.url || null,
            serverElevation: uploadedElevation,
          }).catch(() => {});

          const nextSession = await loadUploadSession(sessionId);
          if (nextSession) {
            const summary = summarizeUploadSession(nextSession);
            if (isMountedRef.current) {
              setUploadSession(summary);
              setUploadProgress(summary.percent);
            }
          }
        } catch (error) {
          console.error('Upload failed for cached file:', error);
          await updateUploadFile(sessionId, latestFile.id, {
            status: 'failed',
            error: error?.message || 'Upload failed',
          }).catch(() => {});

          const shouldPause = isUploadNetworkError(error);
          if (shouldPause) {
            await updateUploadSession(sessionId, { status: 'paused', error: error?.message || 'Connection lost' }).catch(() => {});
            await refreshUploadSessionState(sessionId);
            break;
          }
        }
      }

      const finalSession = await loadUploadSession(sessionId);
      const summary = summarizeUploadSession(finalSession);
      const remaining = summary.pending + summary.failed + summary.uploading;

      if (remaining === 0) {
        await deleteUploadSession(sessionId).catch(() => {});
        releasePreviewObjectUrls();
        if (isMountedRef.current) {
          setUploadSession(null);
          setUploadProgress(100);
        }
      } else {
        await updateUploadSession(sessionId, {
          status: summary.failed > 0 ? 'paused' : 'pending',
          error: summary.failed > 0 ? 'Some files need to be reuploaded from local storage' : '',
        }).catch(() => {});
        await refreshUploadSessionState(sessionId);
      }

      return { completed: remaining === 0, sessionId };
    } finally {
      if (activeUploadRef.current === sessionId) {
        activeUploadRef.current = null;
      }
      if (isMountedRef.current) {
        setIsUploading(false);
      }
    }
  };

  const startUploadSession = async (files, isFolderUpload = false) => {
    const fileArray = sortUploadFilesByName(files);
    if (fileArray.length === 0) return;

    setUploadProgress(0);
    setUploadedImages([]);
    setIsUploading(false);
    setShowUploadBox(false);
    try {
      const session = await createUploadSession({
        type: isFolderUpload ? 'folder' : 'elevation',
        projectId,
        buildingId: getEffectiveBuildingId(),
        imageType: activeImageType,
        elevationName: elevationName || null,
        projectName: projectName || 'Uploaded Project',
        files: fileArray,
      });

      const summary = summarizeUploadSession(session);
      setUploadSession(summary);
      setUploadProgress(summary.percent);
      await setPreviewFromFiles(session.id, fileArray, session.files?.map((item) => item.id) || []);
    } catch (error) {
      console.error('Failed to start cached upload session:', error);
      if (isMountedRef.current) {
        setIsUploading(false);
        setUploadSession(null);
        setUploadProgress(0);
        alert(`Upload failed: ${error?.message || 'Unable to cache files locally'}`);
      }
    }
  };

  const resumeCachedUpload = async () => {
    if (!uploadSession?.id || isUploading) return;
    await processUploadSession(uploadSession.id);
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      releasePreviewObjectUrls();
    };
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    folderStructureRef.current = folderStructure;
  }, [folderStructure]);

  useEffect(() => {
    let cancelled = false;
    const loadPendingSession = async () => {
      try {
        const context = {
          projectId,
          buildingId: selectedBuildingId || buildingId || null,
          imageType: activeImageType,
        };
        const pending = await findLatestPendingUploadSession(context);
        if (cancelled || !isMountedRef.current) return;
        if (!pending) {
          setUploadSession(null);
          return;
        }
        const summary = summarizeUploadSession(pending);
        setUploadSession(summary);
        setUploadProgress(summary.percent);
      } catch (error) {
        console.error('Failed to load cached upload session:', error);
      }
    };
    loadPendingSession();
    return () => {
      cancelled = true;
    };
  }, [projectId, selectedBuildingId, buildingId, activeImageType]);

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      // Check if any of the items are directories
      const hasDirectory = Array.from(e.dataTransfer.items).some(
        item => item.webkitGetAsEntry && item.webkitGetAsEntry().isDirectory
      );
      
      if (hasDirectory) {
        alert('Folder detected! For folder uploads, please use the "Upload Folder" button for better compatibility.');
        return;
      }
    }
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Check if this is a folder upload by checking for webkitdirectory attribute
      const isFolderUpload = e.target.hasAttribute('webkitdirectory');
      handleFiles(files, isFolderUpload);
    }
  };

  const handleFolderSelect = async (event) => {
    console.log('handleFolderSelect called', event.type);
    
    // First try the modern File System Access API if available
    if ('showDirectoryPicker' in window && event.type === 'click') {
      try {
        console.log('Attempting to use modern directory picker');
        const directoryHandle = await window.showDirectoryPicker();
        const files = [];
        
        // Recursively get all files from the directory
        const getFilesRecursively = async (dirHandle, path = '') => {
          for await (const entry of dirHandle.values()) {
            if (entry.kind === 'file') {
              const file = await entry.getFile();
              // Add webkitRelativePath for compatibility
              Object.defineProperty(file, 'webkitRelativePath', {
                value: path ? `${path}/${file.name}` : file.name,
                writable: false
              });
              files.push(file);
            } else if (entry.kind === 'directory') {
              await getFilesRecursively(entry, path ? `${path}/${entry.name}` : entry.name);
            }
          }
        };
        
        await getFilesRecursively(directoryHandle, directoryHandle.name);
        
        if (files.length > 0) {
          console.log('Modern API: Folder upload detected:', files.length, 'files');
          handleFiles(files, true);
        }
        return;
      } catch (error) {
        console.log('Modern directory picker cancelled or failed:', error.message);
        // Fall back to traditional method if user cancelled
        if (error.name === 'AbortError') {
          return;
        }
      }
    }
    
    // Fallback to traditional webkitdirectory method
    console.log('Using traditional webkitdirectory method');
    const files = event.target.files;
    console.log('Files from input:', files ? files.length : 0);
    
    if (files && files.length > 0) {
      // Set a flag to indicate this is a folder upload
      const isFolderUpload = true;
      
      // Log to verify files are being processed
      console.log('Traditional API: Folder upload detected:', files.length, 'files');
      
      // Check if webkitRelativePath exists
      const hasWebkitRelativePath = files[0].webkitRelativePath !== undefined;
      console.log('Has webkitRelativePath:', hasWebkitRelativePath);
      
      if (hasWebkitRelativePath) {
        console.log('Sample webkitRelativePath:', files[0].webkitRelativePath);
      } else {
        console.log('webkitRelativePath not available, using fallback method');
        
        // Create a simple folder structure based on the upload
        const tempFiles = Array.from(files).map(file => {
          // Create a synthetic webkitRelativePath
          file.webkitRelativePath = 'Uploaded Folder/' + file.name;
          return file;
        });
        
        handleFiles(tempFiles, isFolderUpload);
        return;
      }
      
      handleFiles(files, isFolderUpload);
    } else {
      console.log('No files selected or files array is empty');
    }
  };

  const handleFiles = async (files, isFolderUpload = false) => {
    await startUploadSession(files, isFolderUpload);
    return;
  };
  
  const updateProgress = (processed, total) => {
    const progress = Math.round((processed / total) * 100);
    setUploadProgress(progress);
  };

  const generateReport = () => {
    try {
      if (!projectId) {
        alert('Please open from a project to generate a report.');
        return;
      }
      const pid = String(projectId);
      const pname = projectName || '';
      const bid = selectedBuildingId || buildingId || '';
      const params = new URLSearchParams({
        view: 'reports',
        projectId: pid,
        projectName: pname,
        buildingId: String(bid || ''),
        imageType: activeImageType || 'drone',
      });
      const url = `https://report.pxi.maxzrange.com/?${params.toString()}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      console.error('Failed to open report portal', e);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
        {projectName ? `Upload Images - ${projectName}` : 'Upload Building Images'}
      </Typography>

      {/* Image Type Tabs for Drone & Tapping projects */}
      {projectType === 'Drone & Tapping' && (
        <Paper sx={{ mb: 3 }}>
          <Tabs 
            value={activeImageType} 
            onChange={(e, newValue) => {
              setActiveImageType(newValue);
              const nextBid = selectedBuildingId || buildingId || null;
              onNavigate && onNavigate('elevations', { initialImageType: newValue, buildingId: nextBid ? Number(nextBid) : null });
            }}
            indicatorColor="primary"
            textColor="primary"
            centered
          >
            <Tab label="Drone Images" value="drone" />
            <Tab label="Tapping Images" value="tapping" />
          </Tabs>
        </Paper>
      )}

      <Typography variant="body1" color="text.secondary" paragraph>
        {projectName 
          ? `Upload ${activeImageType} images for ${projectName}. The system will automatically detect floors/elevations from filenames or folder structure.`
          : `Drag and drop your ${activeImageType} images here or upload an entire folder. The system will automatically detect floors/elevations from filenames or folder structure. You can upload up to 100 images at once.`
        }
      </Typography>

      {/* Building Selection */}
      {projectId && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Building Selection (Optional)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a building to associate these images with a specific building in your project.
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="building-select-label">Select Building</InputLabel>
            <Select
              labelId="building-select-label"
              value={selectedBuildingId}
              label="Select Building"
              onChange={(e) => {
                const next = e.target.value || '';
                setSelectedBuildingId(next);
                setFolderStructure({});
                setUploadedImages([]);
                setSelectedFolder(null);
                setUploadProgress(0);
                setShowUploadBox(true);
                const bn = buildings.find((b) => String(b.id) === String(next))?.name || null;
                onNavigate && onNavigate('elevations', {
                  buildingId: next ? Number(next) : null,
                  ...(bn ? { buildingName: bn } : {}),
                  initialImageType: activeImageType,
                });
              }}
              disabled={Boolean(isUploading || buildingsLoading)}
            >
              <MenuItem value="">
                <em>No Building (General Project Images)</em>
              </MenuItem>
              {buildings.map((building) => (
                <MenuItem key={building.id} value={building.id}>
                  {building.name} {building.address && `- ${building.address}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {buildings.length === 0 && !buildingsLoading && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No buildings found for this project. A building will be created automatically when you save in the editor.
            </Alert>
          )}
        </Paper>
      )}

      {showUploadBox && (
      <UploadBox
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        sx={{
          borderColor: isDragging ? 'primary.main' : '#ccc',
          backgroundColor: isDragging ? '#f0f7ff' : '#fafafa',
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>
          Drag and drop images here
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Or click to browse your files. Supported formats: JPG, PNG.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            component="label"
            variant="contained"
            sx={{ mt: 2 }}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Browse Files'}
            <VisuallyHiddenInput type="file" multiple accept="image/*" onChange={handleFileSelect} />
          </Button>
          <Button
            component="label"
            variant="outlined"
            sx={{ mt: 2 }}
            disabled={isUploading}
            onClick={async (e) => {
              if (isUploading) return;
              console.log('Folder upload button clicked');
              // Try modern API first
              if ('showDirectoryPicker' in window) {
                console.log('Using modern directory picker API');
                e.preventDefault();
                await handleFolderSelect(e);
              } else {
                console.log('Using traditional webkitdirectory method');
                // Let the label handle the file input click naturally
              }
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Folder'}
            <input
              type="file"
              multiple
              webkitdirectory=""
              directory=""
              style={{ display: 'none' }}
              onChange={handleFolderSelect}
            />
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="text" onClick={() => setShowUploadBox(false)}>
            Hide
          </Button>
        </Box>
      </UploadBox>
      )}

      {!showUploadBox && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, mb: 3 }}>
          <Button variant="outlined" onClick={() => setShowUploadBox(true)}>
            Show Upload
          </Button>
        </Box>
      )}

      {uploadProgress > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle1">Upload Progress</Typography>
            <Typography variant="subtitle1">
              {isUploading ? 'Uploading...' : `${uploadedImages.length} images uploaded`}
            </Typography>
          </Box>
          <LinearProgress
            variant={isUploading && uploadProgress === 0 ? 'indeterminate' : 'determinate'}
            value={uploadProgress}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {uploadSession && uploadSession.status !== 'local' && (
        <Paper sx={{ mt: 4, p: 2, borderRadius: 2, border: '1px solid', borderColor: uploadSession.status === 'paused' ? 'warning.main' : 'divider' }}>
          <Alert
            severity={uploadSession.status === 'paused' ? 'warning' : uploadSession.status === 'local' ? 'info' : 'info'}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={resumeCachedUpload}
                disabled={isUploading || !isOnline}
              >
                {isUploading
                  ? 'Uploading...'
                  : uploadSession.status === 'local'
                    ? 'Upload to server'
                    : 'Reupload from local storage'}
              </Button>
            }
            sx={{ alignItems: 'center' }}
          >
            {isUploading
              ? 'Uploading cached files to the server now.'
              : uploadSession.status === 'local'
              ? 'The folder has been cached locally. Review the elevation preview first, then upload it to the server.'
              : uploadSession.status === 'paused'
              ? 'The upload was paused. The selected files are still cached locally, so you can continue without selecting them again.'
              : 'A cached upload session is available. If the connection drops, you can resume it from local storage.'}
          </Alert>

          <Box sx={{ mt: 2, display: 'grid', gap: 1.5 }}>
            {Object.entries(uploadSession.byElevation || {}).length > 0 ? (
              Object.entries(uploadSession.byElevation || {}).map(([elevationNameKey, stats]) => (
                <Box key={elevationNameKey} sx={{ display: 'grid', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {elevationNameKey}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stats.completed}/{stats.total} uploaded
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={stats.percent || 0}
                    sx={{ height: 8, borderRadius: 999 }}
                  />
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Files are cached locally and will appear here once the first upload completes.
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {uploadedImages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>
            Folder Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Folders are displayed below. Click on a folder tab to view its images.
          </Typography>
          
          <FolderView
            folderStructure={folderStructure}
            selectedFolder={selectedFolder}
            setSelectedFolder={setSelectedFolder}
            projectId={projectId}
            buildingId={selectedBuildingId || buildingId}
            buildingName={buildings.find(b => b.id === (selectedBuildingId || buildingId))?.name}
            onUpdateFolderStructure={(newStructure) => {
              setFolderStructure(newStructure);
              setUploadedImages(
                Object.values(newStructure || {}).flatMap((images) => Array.isArray(images) ? images : [])
              );
            }}
            imageType={activeImageType}
            uploadSession={uploadSession}
            onResumeUpload={resumeCachedUpload}
            previewOnly={uploadSession?.status === 'local'}
          />

          {uploadSession?.status !== 'local' && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  const bid = selectedBuildingId || null;
                  const bn = bid ? (buildings.find((b) => String(b.id) === String(bid))?.name || null) : null;
                  onFolderStructureChange && onFolderStructureChange(folderStructure, bid, activeImageType, bn);
                }}
              >
                Edit in Grid View
              </Button>
              <Button variant="contained" color="primary" onClick={generateReport}>
                Generate Report
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
